import { SPECIFIC_ADDON_PRODUCT_EXCEPTION, ADDON_V2_ERROR_CODES, CARDHOLDER_ERROR_CODES, THIRD_PARTY_ORDER_ERROR_CODES } from '@constants'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { type ResponseErrorGenerics } from '@dni-types/utilities/response'
import { CouponExceptionCode, LifetimeLimitedProductExceptionCode, PaymentTransactionExceptionCode, StockExceptionCode, type CartValidationDetail, type CouponExceptionCodeType, type LifetimeLimitedExceptionCodeType, type PaymentTransactionExceptionCodeType, type StockExceptionCodeType } from '@hooks-api/types/dataModel/coupon'
import { AxiosError } from 'axios'
import { isValidateErrorCode } from '../utils/errorHandlers'

const VALIDATION_ERROR_CODES = {
  INVALID_PARAMETERS: THIRD_PARTY_ORDER_ERROR_CODES.INVALID_PARAMETERS,
} as const

export interface UseHandleCheckoutExceptionArgs {
  handleCouponException?: (code?: CouponExceptionCodeType, detail?: CartValidationDetail) => void
  handleStockException?: (code?: StockExceptionCodeType, detail?: CartValidationDetail) => void
  handleLifetimeLimitedException?: (code?: LifetimeLimitedExceptionCodeType, detail?: CartValidationDetail) => void
  handlePaymentTransactionException?: (code?: PaymentTransactionExceptionCodeType) => void
  handleCheckoutCardholderErrorException?: (error: AxiosError<{ error: ResponseErrorGenerics<unknown> }>) => void
  /**
   * 406 的 API Exception 都會進入這個 callback
   */
  handleCustomerApiException?: (error: AxiosError<{ error: ResponseErrorGenerics<unknown> }>) => void
  /**
   * 所有被 handle 之外的 API Exception 都會進入這個 callback
   */
  handleTheOthersApiException?: (error: AxiosError) => void
  /**
   * 所有未被 handle 的非 API Exception 都會進入這個 callback
   */
  handleUnexpectedException?: (error: unknown) => void
  handleAddonV2Exception?: (code: ResponseErrorGenerics['code'], detail?: ResponseErrorGenerics<CartValidationDetail>['detail']) => void
  handleSpecificAddonProductException?: (code: ResponseErrorGenerics['code'], detail?: ResponseErrorGenerics<CartValidationDetail>['detail']) => void
  /**
   * 400 + INVALID_PARAMETERS (5300) 的驗證錯誤
   */
  handleValidationException?: (detail?: ResponseErrorGenerics['detail']) => void
  /**
   * HTTP 422：門市/取貨點已停用。未傳則 422 落 handleTheOthersApiException（維持其他 caller 原行為）
   */
  handleShippingStoreUnavailableException?: () => void
}

const isAxiosError = (error: unknown): error is AxiosError<{ error: ResponseErrorGenerics }> => {
  return error instanceof AxiosError
}

export const useHandleCheckoutException = (args: UseHandleCheckoutExceptionArgs) => {
  const {
    handleCouponException,
    handleCustomerApiException,
    handleTheOthersApiException,
    handleStockException,
    handleLifetimeLimitedException,
    handlePaymentTransactionException,
    handleUnexpectedException,
    handleAddonV2Exception,
    handleCheckoutCardholderErrorException,
    handleSpecificAddonProductException,
    handleValidationException,
    handleShippingStoreUnavailableException,
  } = args

  /**
   * 1. http code = 406, 直接顯示後端給的 error message
   * 2. http code = 400, 判斷 error code 是否在清單, 決定對應的顯示方式
   * 3. 其他狀況, 顯示預設錯誤訊息
   */
  const handleCheckoutException = useCallbackEvent((error: unknown) => {
    if (isAxiosError(error)) {
      const errorData = error.response?.data.error as ResponseErrorGenerics<CartValidationDetail> | undefined
      if (error.response?.status === 400 && errorData?.code) {
        if (isValidateErrorCode(errorData.code, CouponExceptionCode)) {
          handleCouponException?.(errorData.code, errorData.detail)
        }
        else if (isValidateErrorCode(errorData.code, StockExceptionCode)) {
          handleStockException?.(errorData.code, errorData.detail)
        }
        else if (isValidateErrorCode(errorData.code, LifetimeLimitedProductExceptionCode)) {
          handleLifetimeLimitedException?.(errorData.code, errorData.detail)
        }
        else if (isValidateErrorCode(errorData.code, PaymentTransactionExceptionCode)) {
          handlePaymentTransactionException?.(errorData.code)
        }
        else if (isValidateErrorCode(errorData.code, ADDON_V2_ERROR_CODES)) {
          handleAddonV2Exception?.(errorData.code, errorData.detail)
        }
        else if (isValidateErrorCode(errorData.code, SPECIFIC_ADDON_PRODUCT_EXCEPTION)) {
          handleSpecificAddonProductException?.(errorData.code, errorData.detail)
        }
        else if (isValidateErrorCode(errorData.code, CARDHOLDER_ERROR_CODES)) {
          handleCheckoutCardholderErrorException?.(error)
        }
        else if (isValidateErrorCode(errorData.code, VALIDATION_ERROR_CODES)) {
          handleValidationException?.(error.response?.data.error.detail)
        }
        else {
          handleTheOthersApiException?.(error)
        }
      }
      else if (error.response?.status === 406) {
        handleCustomerApiException?.(error)
      }
      else if (error.response?.status === 422 && handleShippingStoreUnavailableException) {
        handleShippingStoreUnavailableException()
      }
      else {
        handleTheOthersApiException?.(error)
      }
    }
    else {
      handleUnexpectedException?.(error)
    }
  })

  return {
    handleCheckoutException,
  }
}
